<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useAudio } from '@/composables/useAudio'
import { usePlayback } from '@/composables/usePlayback'
import { useExport } from '@/composables/useExport'
import { usePageHead } from '@/composables/usePageHead'
import { t, currentSpoke, pageId } from '@/i18n'
import { trackEvent } from '@/utils/analytics'
import type { TimeRange, ExportFormat, EditMode } from '@/types'

// Layout Components (static - pre-rendered by SSG)
import AppHeader from '@/components/layout/AppHeader.vue'
import AppFooter from '@/components/layout/AppFooter.vue'

// Static imports for SSG-rendered components
import InstructionsSection from '@/components/instructions/InstructionsSection.vue'
import TrustBadges from '@/components/TrustBadges.vue'
import FaqSection from '@/components/FaqSection.vue'
import SpokeInternalLinks from '@/components/SpokeInternalLinks.vue'
import ContactSection from '@/components/ContactSection.vue'

// Interactive components (only needed client-side)
import FileUpload from '@/components/FileUpload.vue'
import AudioConverter from '@/components/AudioConverter.vue'
import AudioEditor from '@/components/editor/AudioEditor.vue'
import AdsenseAd from '@/components/AdsenseAd.vue'
import Toast from '@/components/Toast.vue'

// SEO head management
usePageHead()

const {
  audioFile,
  audioBuffer,
  waveformData,
  isLoading,
  error: audioError,
  loadAudioFile,
  extractSegment,
  getAudioContext
} = useAudio()

// Playback state
const {
  isPlaying,
  currentTime,
  loop,
  setAudioBuffer,
  setSelection,
  setEditMode,
  togglePlay,
  stop,
  seek,
  toggleLoop
} = usePlayback(getAudioContext)

// Export state
const {
  isExporting,
  exportProgress,
  exportAudio,
  cancelExport,
  cleanup: cleanupExport
} = useExport()

// Selection state
const selection = ref<TimeRange>({ start: 0, end: 0 })

// Edit mode state (cut = keep selection, trim = remove selection)
const editMode = ref<EditMode>('cut')

// Toast notifications
const toastMessage = ref('')
const toastType = ref<'success' | 'error' | 'warning'>('success')
const showToast = ref(false)
const newFileInput = ref<HTMLInputElement | null>(null)
let toastTimeoutId: ReturnType<typeof setTimeout> | null = null

// Computed
const duration = computed(() => audioFile.value?.duration || 0)
const hasAudio = computed(() => !!audioFile.value)

// Length of the exported audio: the selection when cutting, the remainder when trimming
const MIN_EXPORT_SECONDS = 0.05
const exportDuration = computed(() => {
  const selected = selection.value.end - selection.value.start
  return editMode.value === 'cut' ? selected : duration.value - selected
})

// Dedicated converter tool (no waveform/trimming) on converter spoke pages
const isConverterPage = computed(() => pageId.value === 'mp3-wav-converter')

// Show toast notification
const displayToast = (message: string, type: 'success' | 'error' | 'warning' = 'success') => {
  if (toastTimeoutId) clearTimeout(toastTimeoutId)
  toastMessage.value = message
  toastType.value = type
  showToast.value = true
  toastTimeoutId = setTimeout(() => {
    showToast.value = false
    toastTimeoutId = null
  }, 3000)
}

// Handle file selection
const handleFileSelect = async (file: File) => {
  trackEvent('file_upload', {
    file_type: file.type,
    file_size: file.size,
    file_extension: file.name.split('.').pop()
  })

  try {
    await loadAudioFile(file)

    if (audioBuffer.value) {
      setAudioBuffer(audioBuffer.value)
      const duration = audioBuffer.value.duration
      // Spoke pages may preset the selection:
      // - presetSelectionSeconds: first N seconds (e.g. WhatsApp 30s status limit)
      // - presetFullSelection: the whole file (e.g. converter pages - no trimming needed)
      const spoke = currentSpoke.value
      const preset = spoke?.presetSelectionSeconds
      if (preset && duration > preset) {
        selection.value = { start: 0, end: preset }
      } else if (spoke?.presetFullSelection) {
        selection.value = { start: 0, end: duration }
      } else {
        selection.value = { start: duration / 3, end: (duration * 2) / 3 }
      }
      setSelection(selection.value)
      // Automatically start playback after loading
      togglePlay()
    }
  } catch (err) {
    const errorKey = audioError.value || 'invalidFile'
    const errorMsg = (t.value as unknown as Record<string, unknown>)[errorKey]
    displayToast(typeof errorMsg === 'string' ? errorMsg : t.value.invalidFile, 'error')
    trackEvent('file_upload_error', { error: String(err) })
  }
}

// Handle transition to new file
const handleNewFileInput = (event: Event) => {
  const input = event.target as HTMLInputElement
  const files = input.files
  if (files && files.length > 0) {
    handleFileSelect(files[0])
  }
  // Reset input value to allow selecting the same file again if needed
  input.value = ''
}

// Handle selection change
const handleSelectionChange = (newSelection: TimeRange) => {
  selection.value = newSelection
  setSelection(newSelection)
}

// Handle seek
const handleSeek = (time: number) => {
  seek(time)
}

// Handle edit mode toggle
const handleToggleMode = () => {
  const newMode = editMode.value === 'cut' ? 'trim' : 'cut'
  editMode.value = newMode
  setEditMode(newMode)
}

// Handle replay - seek to start of playable content
const handleReplay = () => {
  // In cut mode, seek to selection start; in trim mode, seek to beginning
  seek(editMode.value === 'cut' ? selection.value.start : 0)
}

// Handle export
const handleExport = async (format: ExportFormat) => {
  if (!audioFile.value || isExporting.value) return

  // Nothing would be left to export (e.g. trim selection covers the whole file)
  if (exportDuration.value < MIN_EXPORT_SECONDS) {
    displayToast(t.value.exportError, 'warning')
    return
  }

  trackEvent('export_start', {
    format,
    duration: selection.value.end - selection.value.start
  })

  // Pause playback during export for better performance
  const wasPlaying = isPlaying.value
  if (wasPlaying) {
    stop()
  }

  try {
    // Extract the segment based on edit mode
    const channels = extractSegment(selection.value, editMode.value)
    const sampleRate = audioFile.value.sampleRate

    await exportAudio(channels, sampleRate, format, audioFile.value.name)

    displayToast(t.value.exportSuccess, 'success')
    trackEvent('export_complete', { format })
  } catch (err) {
    if (import.meta.env.DEV) console.error('Export error:', err)
    // Only show error if not cancelled
    if (err instanceof Error && err.message !== 'Encoding cancelled') {
      displayToast(t.value.exportError, 'error')
      trackEvent('export_error', { format, error: err.message })
    } else {
      trackEvent('export_cancel', { format })
    }
  }
}

// Reset application - now triggers new file selection
const handleReset = () => {
  stop() // Stop playback if any
  newFileInput.value?.click()
}

// Watch for audio buffer changes
watch(audioBuffer, (buffer: AudioBuffer | null) => {
  if (buffer) {
    setAudioBuffer(buffer)
  }
})

// Watch for selection changes (selection ref is replaced, not mutated, so deep is unnecessary)
watch(selection, (newSelection: TimeRange) => {
  setSelection(newSelection)
})

// Keyboard shortcuts
const handleKeydown = (event: KeyboardEvent) => {
  if (!hasAudio.value) return
  if (event.ctrlKey || event.metaKey || event.altKey) return

  const target = event.target as HTMLElement | null
  // Never steal keys from text fields (e.g. the time label editor)
  if (target?.closest('input, textarea, select, [contenteditable="true"]')) return
  // Space activates a focused button/link/menu item - don't turn it into play/pause
  if (event.code === 'Space' && target?.closest('button, a, [role="button"], [role="menuitem"], [role="radio"]')) return

  switch (event.code) {
    case 'Space':
      event.preventDefault()
      togglePlay()
      break
    case 'Escape':
      stop()
      break
    case 'KeyL':
      toggleLoop()
      break
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleKeydown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown)
  cleanupExport()
  if (toastTimeoutId) clearTimeout(toastTimeoutId)
})
</script>

<template>
  <div class="app">
    <!-- Skip to content link for keyboard users -->
    <a href="#main-content" class="skip-to-content">{{ t.skipToContent }}</a>

    <!-- Header -->
    <AppHeader />

    <!-- Main Content -->
    <main id="main-content" class="main" aria-label="Audio Editor">
      <div class="container">
        <!-- File Upload / Editor Area (client-only interactive) -->
        <ClientOnly>
          <div class="editor-wrapper">
            <!-- Dedicated converter (no trimming) on converter spoke pages -->
            <AudioConverter v-if="isConverterPage" />

            <template v-else>
              <!-- File Upload -->
              <FileUpload
                v-if="!hasAudio"
                :is-loading="isLoading"
                @file-select="handleFileSelect"
              />

              <!-- Audio Editor -->
              <AudioEditor
                v-else
                :waveform-data="waveformData"
                :duration="duration"
                :selection="selection"
                :current-time="currentTime"
                :filename="audioFile?.name"
                :is-playing="isPlaying"
                :loop="loop"
                :is-exporting="isExporting"
                :export-progress="exportProgress"
                :edit-mode="editMode"
                @update:selection="handleSelectionChange"
                @seek="handleSeek"
                @toggle-play="togglePlay"
                @replay="handleReplay"
                @toggle-loop="toggleLoop"
                @toggle-mode="handleToggleMode"
                @export="handleExport"
                @cancel-export="cancelExport"
                @reset="handleReset"
              />
            </template>
          </div>

          <template #fallback>
            <div class="editor-wrapper">
              <div class="upload-placeholder card">
                <p class="upload-placeholder-text">{{ t.uploadPrompt }}</p>
                <div class="btn btn-primary btn-lg">{{ t.uploadButton }}</div>
                <p class="upload-placeholder-formats">{{ t.supportedFormats }}</p>
              </div>
            </div>
          </template>
        </ClientOnly>

        <!-- Privacy Trust Badges -->
        <TrustBadges />

        <!-- Instructions Section -->
        <InstructionsSection />

        <!-- Ad after Instructions (client-only) -->
        <ClientOnly>
          <AdsenseAd ad-slot="1094897978" />
        </ClientOnly>

        <!-- FAQ Section -->
        <FaqSection />

        <!-- Related Tools / Internal Links -->
        <SpokeInternalLinks />

        <!-- Contact Section -->
        <ContactSection />
      </div>
    </main>

    <!-- Footer -->
    <AppFooter />

    <!-- Toast (client-only) -->
    <ClientOnly>
      <Toast
        v-if="showToast"
        :message="toastMessage"
        :type="toastType"
      />
    </ClientOnly>

    <!-- Hidden file input for "New File" action (client-only) -->
    <ClientOnly>
      <input
        ref="newFileInput"
        type="file"
        accept="audio/*,.mp3,.wav,.ogg,.aac,.flac,.m4a"
        style="display: none"
        @change="handleNewFileInput"
      />
    </ClientOnly>
  </div>
</template>

<style scoped>
.app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.main {
  flex: 1;
  padding: 2rem 0;
}

.editor-wrapper {
  /* Match FileUpload min-height to prevent layout shift (CLS) */
  /* min-height: 339px; */
  /* Reserve space immediately to prevent layout shift */
  contain: layout style;
}

/* SSG placeholder that mirrors FileUpload styling */
.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 339px;
  padding: 2rem;
  border: 3px dashed var(--border-color);
  border-radius: var(--radius-xl);
  text-align: center;
  gap: 1rem;
}

.upload-placeholder-text {
  font-size: 1.125rem;
  font-weight: 500;
  color: var(--text-primary);
  margin: 0;
}

.upload-placeholder-formats {
  color: var(--text-secondary);
  font-size: 0.875rem;
  margin: 0;
}

@media (max-width: 640px) {
  .main {
    padding: 1rem 0;
  }

  .upload-placeholder {
    min-height: 250px;
    padding: 1.25rem 0.75rem;
  }
}
</style>
